// src/components/organisms/integrations/salesforce/create/automation-context.tsx
import { SalesforceAutomationLocalData } from 'module/api/integrations/salesforce/types';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useRef,
} from 'react';

interface AutomationContextType {
  automationDetails: Partial<SalesforceAutomationLocalData> | undefined;
  setAutomationDetails: (
    updates: Partial<SalesforceAutomationLocalData>,
  ) => void;
  isContactSelected: boolean;
  setIsContactSelected: (isContactSelected: boolean) => void;
  isContactSyncConfigured: boolean;
  setIsContactSyncConfigured: (isConfigured: boolean) => void;
}

const AutomationContext = createContext<AutomationContextType | undefined>(
  undefined,
);

export const useAutomationContext = () => {
  const context = useContext(AutomationContext);

  if (!context) {
    throw new Error(
      'useAutomationContext must be used within an AutomationProvider',
    );
  }

  return context;
};

interface AutomationProviderProps {
  children: ReactNode;
}

export const AutomationProvider: React.FC<AutomationProviderProps> = ({
  children,
}) => {
  const [automationDetails, setAutomationDetails] = useState<
    Partial<SalesforceAutomationLocalData> | undefined
  >();

  const [isContactSelected, setIsContactSelected] = useState(false);
  const [isContactSyncConfigured, setIsContactSyncConfigured] = useState(true);

  // Keep track of previous values to detect changes
  const previousValuesRef = useRef<Partial<SalesforceAutomationLocalData>>({});

  // Track if this is the initial load (to skip reset logic for edit mode)
  const isInitialLoadRef = useRef(true);

  const updateAutomationDetails = useCallback(
    (updates: Partial<SalesforceAutomationLocalData>) => {
      setAutomationDetails((prev) => {
        const prevValues = prev || {};
        const fieldsToReset: Partial<SalesforceAutomationLocalData> = {};

        // Skip reset logic on first data load to support edit mode
        // In edit mode, all existing data is loaded at once - we don't want to reset fields
        // In create mode, the first field entry shouldn't trigger resets either
        const isFirstDataLoad = !prev || Object.keys(prevValues).length === 0;

        if (isInitialLoadRef.current && isFirstDataLoad) {
          // Mark initial load as complete after first data is set
          if (updates && Object.keys(updates).length > 0) {
            isInitialLoadRef.current = false;
          }

          // Skip reset logic on initial data load
          return {
            ...prevValues,
            ...updates,
          };
        }

        // Only apply reset logic after initial load is complete
        // Rule 1: If salesforceObject changes, reset ALL downstream fields and drawer data
        if (
          updates.salesforceObject &&
          prevValues.salesforceObject?.value !== updates.salesforceObject?.value
        ) {
          fieldsToReset.salesForceType = undefined;
          fieldsToReset.triggers = undefined;
          fieldsToReset.matches = undefined;
          fieldsToReset.rocketlaneFields = undefined;
          fieldsToReset.accountFields = undefined;
          fieldsToReset.contactFields = undefined;
          fieldsToReset.sourceResourceType = undefined;
          fieldsToReset.sourceRelationshipName = undefined;
          fieldsToReset.sourceParentResourceId = undefined;
          fieldsToReset.sourceParentResourceType = undefined;
          fieldsToReset.sourceParentResourceFieldId = undefined;
          fieldsToReset.actions = undefined;
        }

        // Trigger and matching conditions are independent; do not reset matches
        // when triggers are updated.

        // Rule 3: If salesForceType (Project/Account) changes, reset field mappings
        if (
          updates.salesForceType &&
          prevValues.salesForceType !== updates.salesForceType
        ) {
          fieldsToReset.rocketlaneFields = undefined;
          fieldsToReset.accountFields = undefined;
          fieldsToReset.contactFields = undefined;
          fieldsToReset.sourceResourceType = undefined;
          fieldsToReset.sourceRelationshipName = undefined;
          fieldsToReset.sourceParentResourceId = undefined;
          fieldsToReset.sourceParentResourceType = undefined;
          fieldsToReset.sourceParentResourceFieldId = undefined;
          fieldsToReset.actions = undefined;
        }

        // Rule 4: If contact sourceResourceType changes, reset contact fields
        if (
          updates.sourceResourceType !== undefined &&
          prevValues.sourceResourceType !== updates.sourceResourceType
        ) {
          const nextContactObject = updates.contactFields?.contactObject;
          const prevContactObject = prevValues.contactFields?.contactObject;

          fieldsToReset.contactFields =
            nextContactObject != null
              ? { contactObject: nextContactObject }
              : prevContactObject != null
                ? { contactObject: prevContactObject }
                : undefined;
        }

        // ✅ NEW RULE 5: If companyObject changes in project flow, reset account and contact fields
        // Handle both partial updates and full rocketlaneFields replacement
        if (updates.rocketlaneFields) {
          const newCompanyObjectValue =
            updates.rocketlaneFields.companyObject?.valueStr;
          const oldCompanyObjectValue =
            prevValues.rocketlaneFields?.companyObject?.valueStr;

          // Check if companyObject has changed (only if we had a previous value to compare)
          // This prevents triggering on initial load but catches all real changes
          if (
            newCompanyObjectValue !== oldCompanyObjectValue &&
            oldCompanyObjectValue !== undefined
          ) {
            fieldsToReset.accountFields = undefined;
            fieldsToReset.contactFields = undefined;
            fieldsToReset.sourceResourceType = undefined;
            fieldsToReset.sourceRelationshipName = undefined;
            fieldsToReset.sourceParentResourceId = undefined;
            fieldsToReset.sourceParentResourceType = undefined;
            fieldsToReset.sourceParentResourceFieldId = undefined;
            fieldsToReset.actions = undefined; // ✅ Clear cached actions to prevent repopulation
          }
        }

        // Store current values for next comparison
        previousValuesRef.current = { ...prevValues, ...updates };

        // Merge updates with reset fields (reset fields take precedence for undefined values)
        return {
          ...prevValues,
          ...updates,
          ...fieldsToReset,
        };
      });
    },
    [],
  );

  return (
    <AutomationContext.Provider
      value={{
        automationDetails,
        isContactSelected,
        setIsContactSelected,
        setAutomationDetails: updateAutomationDetails,
        isContactSyncConfigured,
        setIsContactSyncConfigured,
      }}
    >
      {children}
    </AutomationContext.Provider>
  );
};
